import { subWeeks } from 'date-fns';
import { seededRandom } from 'lib_ts/classes/math.utilities';
import { MetricInterval } from 'lib_ts/enums/machine-models.enums';
import {
  IModelPrediction,
  IPredictionAccuracy,
} from 'lib_ts/interfaces/modelling/i-eval-models';
import {
  IMachinePrecision,
  IPerformanceAccuracy,
  IRealMachineMetric,
  IRealMachinePerformance,
  IRealModelPerformance,
} from 'lib_ts/interfaces/modelling/i-real-machine-metric';

const DEMO_MACHINE_ID = 'demo-machine';

/* ---------------------------------------------------------------- *
 * Machine + model performance
 *
 * The radars and scatter plots convert raw error values into a 0-100%
 * rating against the good/bad thresholds in MachineMetricHelper, so these
 * numbers are chosen to land mid-range rather than pegged at 100%.
 * ---------------------------------------------------------------- */

const scaleStdev = (
  base: IMachinePrecision,
  factor: number
): IMachinePrecision => ({
  ...base,
  plx: base.plx * factor,
  plz: base.plz * factor,
  vy: base.vy * factor,
  wx: base.wx * factor,
  wy: base.wy * factor,
  wz: base.wz * factor,
  break_x_ft: base.break_x_ft * factor,
  break_z_ft: base.break_z_ft * factor,
});

const scaleAccuracy = <T extends IPredictionAccuracy>(
  base: T,
  factor: number
): T => ({
  ...base,
  vx: base.vx * factor,
  vy: base.vy * factor,
  vz: base.vz * factor,
  wx: base.wx * factor,
  wy: base.wy * factor,
  wz: base.wz * factor,
  break_x_ft: base.break_x_ft * factor,
  break_z_ft: base.break_z_ft * factor,
});

const DEMO_GROUPS = 42;
const DEMO_SHOTS = 336;

const DEMO_MEAN_STDEV: IMachinePrecision = {
  groups: DEMO_GROUPS,
  shots: DEMO_SHOTS,
  plx: 0.24,
  plz: 0.26,
  plx_gt6in: 3,
  plz_gt6in: 5,
  vy: 0.48,
  wx: 235,
  wy: 120,
  wz: 245,
  break_x_ft: 0.19,
  break_z_ft: 0.2,
};

const DEMO_PREDICTION_ERR: IPredictionAccuracy = {
  vx: 0.5,
  vy: 0.7,
  vz: 0.45,
  wx: 180,
  wy: 110,
  wz: 210,
  break_x_ft: 0.25,
  break_z_ft: 0.24,
};

const DEMO_MEAN_ABSOLUTE_ERR: IPerformanceAccuracy = {
  ...DEMO_PREDICTION_ERR,
  groups: DEMO_GROUPS,
  shots: DEMO_SHOTS,
};

const buildMachinePerformance = (factor: number): IRealMachinePerformance => ({
  groups: DEMO_GROUPS,
  shots: DEMO_SHOTS,
  plx_gt6in: DEMO_MEAN_STDEV.plx_gt6in,
  plz_gt6in: DEMO_MEAN_STDEV.plz_gt6in,
  mean_stdev: scaleStdev(DEMO_MEAN_STDEV, factor),
  median_stdev: scaleStdev(DEMO_MEAN_STDEV, factor * 0.87),
  max_stdev: scaleStdev(DEMO_MEAN_STDEV, factor * 1.38),
});

const buildModelPerformance = (factor: number): IRealModelPerformance => ({
  groups: DEMO_GROUPS,
  shots: DEMO_SHOTS,
  mean_absolute_err: scaleAccuracy(DEMO_MEAN_ABSOLUTE_ERR, factor),
  median_absolute_err: scaleAccuracy(DEMO_MEAN_ABSOLUTE_ERR, factor * 0.82),
  max_absolute_err: scaleAccuracy(DEMO_MEAN_ABSOLUTE_ERR, factor * 1.95),
});

// the radar charts are temporarily out of the demo; these are kept so they can
// be dropped back in without re-tuning the values against the thresholds above
export const DEMO_MACHINE_PERFORMANCE = buildMachinePerformance(1);

export const DEMO_MODEL_PERFORMANCE = buildModelPerformance(1);

export const DEMO_MODEL_PREDICTION: IModelPrediction = {
  mean_absolute_err: DEMO_PREDICTION_ERR,
  median_absolute_err: DEMO_PREDICTION_ERR,
  max_absolute_err: scaleAccuracy(DEMO_PREDICTION_ERR, 1.95),
  mean_best_90p: scaleAccuracy(DEMO_PREDICTION_ERR, 0.85),
  mean_worst_10p: scaleAccuracy(DEMO_PREDICTION_ERR, 1.9),
};

export const DEMO_METRIC_TIMESTAMP = new Date().toISOString();

const WEEKS_OF_METRICS = 12;

const buildDemoMetric = (config: {
  index: number;
  date: Date;
  factor: number;
}): IRealMachineMetric => {
  const iso = config.date.toISOString();

  return {
    _id: `demo-metric-${config.index}`,
    _created: iso,
    _changed: iso,
    _parent_id: DEMO_MACHINE_ID,
    _parent_def: 'machines',
    job_interval: MetricInterval.Weekly,
    dataFilter: {
      machineID: DEMO_MACHINE_ID,
      end_date: iso,
      limit: 500,
      training: null,
      check_confidence: true,
      check_spin: true,
      check_breaks: true,
    },
    shots: DEMO_SHOTS,
    active_model_name: `demo-model-${config.index}`,
    machine_performance: buildMachinePerformance(config.factor),
    model_performance: buildModelPerformance(config.factor),
  };
};

/** weekly metrics trending from mediocre toward good, with enough noise to look real */
export const DEMO_MACHINE_METRICS: IRealMachineMetric[] = (() => {
  const random = seededRandom(11);

  return Array.from({ length: WEEKS_OF_METRICS }, (_, index) => {
    const progress = index / (WEEKS_OF_METRICS - 1);

    return buildDemoMetric({
      index,
      date: subWeeks(new Date(), WEEKS_OF_METRICS - 1 - index),
      // smaller factor => smaller error => better rating
      factor: 1.35 - 0.5 * progress + (random() - 0.5) * 0.12,
    });
  });
})();
